/**
 * The *kinds* of thing that are shot at but do not fly, and the shape of the
 * record that describes one — the vocabulary half of `target-profiles.ts`.
 *
 * Split out when the searchlight arrived and the catalog went past its size
 * ceiling, and the division is a real one rather than a filing convenience.
 * This file is **the vocabulary and the form**: which kinds exist, which group
 * each belongs to, what ammunition there is, what a debrief column can be, and
 * what fields a `TargetProfile` has. It imports nothing at all, so anything may
 * depend on it — which is what lets `searchlight-posts.ts` name a kind without
 * pulling in the whole table.
 *
 * `target-profiles.ts` next door is **the catalog**: the six filled-in records
 * and the provenance for every number in them. The evidence for the four groups
 * below lives in that file's module note, because that is where the numbers it
 * justifies are.
 */

/** Gasbags. Both have real baked geometry (`VOLUME.005/BALLOON.TBL`, `ZEPPELIN.TBL`). */
export const AIRBORNE_TARGET_KINDS = ["balloon", "zeppelin"] as const;

/**
 * The three *structures* a raid is flown against — the ones the manual names.
 * Deliberately just these three: this list is what `raid-parts.ts` places and
 * what the raid tests pin, so the train does not belong in it.
 */
export const GROUND_TARGET_KINDS = ["supplyDump", "factory", "railYard"] as const;

/**
 * Ground targets that *move*. The original has exactly one — object class 5,
 * `"TRAIN"` — and so do we; see `ground-route.ts` for the recovered mechanics
 * and `target-profiles.ts`'s module note for why `SUPPLY TRUCK`/`TANK` are not
 * here.
 */
export const MOBILE_GROUND_TARGET_KINDS = ["train"] as const;

/**
 * Things that belong to a *defence* rather than to the war effort — a battery's
 * own kit, standing where its guns stand. Deliberately its own list rather than
 * a fourth member of `GROUND_TARGET_KINDS`: that list is what a bombing raid is
 * flown against and what `raid-parts.ts` places, and a searchlight is neither.
 *
 * The one member is a **port extension**, on the same terms AD-017 §9 states
 * for the beams: the original draws no searchlight, gives a battery no health,
 * and places no lamp anywhere in the 804-object corpus. What this port adds is
 * the hardware that makes the extension answerable — a light you can see is a
 * light you should be able to shoot out, and shooting it out is what AD-017 §11
 * makes the guns behind it pay for.
 */
export const DEFENCE_TARGET_KINDS = ["searchlight"] as const;

export const TARGET_KINDS = [
  ...AIRBORNE_TARGET_KINDS,
  ...GROUND_TARGET_KINDS,
  ...MOBILE_GROUND_TARGET_KINDS,
  ...DEFENCE_TARGET_KINDS,
] as const;

export type AirborneTargetKind = (typeof AIRBORNE_TARGET_KINDS)[number];
export type GroundTargetKind = (typeof GROUND_TARGET_KINDS)[number];
export type MobileGroundTargetKind = (typeof MOBILE_GROUND_TARGET_KINDS)[number];
export type DefenceTargetKind = (typeof DEFENCE_TARGET_KINDS)[number];
export type TargetKind = (typeof TARGET_KINDS)[number];

export type TargetClass = "airborne" | "ground";

/**
 * The two ammunition types, re-exported from `flight/guns.ts`.
 *
 * They lived here while the belt only ever decided how much integrity a gasbag
 * lost. G35 gave it a second consumer — the ammunition-indexed jam threshold —
 * which is inside the sim, and `flight/` may not import from `combat/`. So the
 * declaration moved to the module whose own header already claimed it ("this
 * module owns ammunition, the burst counter, and the jam state machine") and
 * this re-export keeps every existing importer working unchanged.
 */
export { AMMUNITION_TYPES, type AmmunitionType } from "../flight/guns.ts";
import type { AmmunitionType } from "../flight/guns.ts";

/**
 * Which debrief column a kill lands in (`PS.EXE`: AIRCRAFT / BALLOONS /
 * ZEPPELINS). `null` for a ground target: the original's debriefing screen has
 * no column for one, and a burnt-out supply dump was never an air victory.
 */
export type TargetKillCategory = "balloons" | "zeppelins" | null;

export interface TargetProfile {
  readonly kind: TargetKind;
  readonly targetClass: TargetClass;
  readonly displayName: string;
  /**
   * The baked model id, matching `data/baked/manifest.json`, or `null` for a
   * kind this port draws some other way.
   *
   * `null` has exactly one member and it is not a gap: the searchlight's
   * hardware is `tools/blender/searchlight.py`'s projector out of
   * `data/enhanced`, instanced by `three/searchlight-mount.ts` with its own
   * articulation, because there is no `.TBL` to bake — the original ships no
   * such object (AD-017 §9). Nullable rather than pointed at some stand-in from
   * the corpus, so every consumer has to *say* what it does about a target kind
   * that is not one of the game's own models.
   */
  readonly bakedModelId: string | null;
  /**
   * Target size in the same raw units `.DAT` offset 140 uses, so gunnery can
   * treat a balloon exactly like an aeroplane. Derived from the baked model's
   * own bounding radius over `targetRadiusMetresPerUnit` (0.035 m/unit):
   * 13.4 m → ~380 for the balloon, 56.2 m → ~1600 for the Zeppelin.
   */
  readonly targetSizeRaw: number;
  /** Ground speed, m/s. A balloon is tethered; a Zeppelin cruises; a factory does neither. */
  readonly driftSpeedMps: number;
  /** Integrity lost per machine-gun hit, by ammunition type. CALIBRATION-PENDING. */
  readonly damagePerHit: Readonly<Record<AmmunitionType, number>>;
  /**
   * Fraction of `bombDamageAtCentre` this kind loses to a bomb that lands on it.
   * Zero for a gasbag: a bomb is not how one is brought down, and pretending it
   * were would make every raid double as a balloon-busting sortie.
   */
  readonly bombVulnerability: number;
  /** Ground extent, metres, for the blast falloff and for standing the model on its footprint. */
  readonly footprintRadiusM: number;
  /** Height of the model's centre above the ground it stands on. Zero for anything airborne. */
  readonly centreHeightM: number;
  /** Seconds it burns before it is gone — the flamer everyone came to see. */
  readonly burnSeconds: number;
  /** Descent rate while burning, m/s. Zero for anything already on the ground. */
  readonly burnDescentMps: number;
  readonly killCategory: TargetKillCategory;
  /**
   * Whether this kind runs a `ground-route.ts` route rather than drifting on a
   * heading. True only for the train, which is the original's only class-5
   * object; everything else keeps the `driftSpeedMps` behaviour.
   */
  readonly followsRoute: boolean;
  /**
   * Whether it trails smoke from the moment it exists. The train's constructor
   * (`pobj.c+0x424`) attaches its plume unconditionally, before anything has
   * happened to it — a Red Baron train is a *smoking* train, and that plume is
   * how you find one from height.
   */
  readonly emitsSmoke: boolean;
  /**
   * Whether anything is *decided* by whether this is still standing.
   *
   * True for everything the war is fought over — the gasbags, the three raid
   * structures, the train — and false for a searchlight, which is a battery's
   * own equipment. Three places ask, and all three would be wrong without it:
   * `encounter.ts`'s `sideStanding` (a side is not still in the fight because
   * its lights are on), `mission-run.ts`'s `groundTally` (a shot-out lamp is
   * not a levelled factory and must not score as one), and the briefing map,
   * which marks objectives and not fittings.
   *
   * Shooting one is still worth doing — it blinds the guns behind it, which is
   * AD-017 §11 — but that payment is made in flak accuracy, not in the score.
   */
  readonly strategic: boolean;
}
